/**
 * Action Definitions and Column Creation
 *
 * This module provides:
 * - Standard action definitions (copy, edit, delete, read)
 * - Bulk action definitions (for Selected component)
 * - Helper functions for action text and icons
 * - createStandardActionsColumn function for table action columns
 */

import type { ColumnDef, CellContext } from '@tanstack/react-table';
import type { ReactNode } from 'react';
import { IconButton } from 'components';
import { Tooltip } from 'components/tooltip';
import { AccessControl } from 'features/accessControl';
import { t } from '@lingui/macro';
import { btnText, BtnIcon } from '../../components/Selected';
import { ActionsCell } from '../styles';
import type { StandardActionId, ActionsConfig } from '../types';
import type { SelectedOptions } from '../../types';

// ============================================================================
// Types
// ============================================================================

type ActionConfig<T> = {
  id: string;
  icon: React.ComponentType<{ size?: number | string; color?: string }>;
  tooltip: ReactNode;
  onClick: (row: T) => void;
  color?: 'error' | 'primary' | 'secondary';
  requiresAccess?: { scope: string; write?: boolean };
};

type BulkActionId = SelectedOptions; // 'delete' | 'copy' | 'read' | 'unread' | 'archive' | 'restore' | 'edit' | 'cancel'

type StandardActionConfig = {
  id: StandardActionId;
  icon: React.ComponentType<Record<string, unknown>>;
  tooltip: ReactNode;
  color?: 'error' | 'primary' | 'secondary';
};

type BulkActionConfig = {
  id: BulkActionId;
  icon: React.ComponentType<Record<string, unknown>>;
  tooltip: ReactNode;
};

// ============================================================================
// Standard Action Definitions
// ============================================================================

/**
 * Row-level actions (for table action columns)
 */
export const STANDARD_ACTIONS: Record<StandardActionId, StandardActionConfig> = {
  copy: {
    id: 'copy',
    icon: BtnIcon.copy,
    tooltip: btnText.copy,
  },
  edit: {
    id: 'edit',
    icon: BtnIcon.edit,
    tooltip: btnText.edit,
  },
  delete: {
    id: 'delete',
    icon: BtnIcon.delete,
    tooltip: btnText.delete,
    color: 'error',
  },
  read: {
    id: 'read',
    icon: BtnIcon.read,
    tooltip: btnText.read,
  },
};

// ============================================================================
// Bulk Action Definitions
// ============================================================================

/**
 * Bulk actions (for Selected component - header actions on selected rows)
 * Imported from Selected.tsx to avoid duplication - Selected.tsx is the source of truth
 */
export const BULK_ACTIONS: Record<BulkActionId, BulkActionConfig> = {
  delete: {
    id: 'delete',
    icon: BtnIcon.delete,
    tooltip: btnText.delete,
  },
  copy: {
    id: 'copy',
    icon: BtnIcon.copy,
    tooltip: btnText.copy,
  },
  read: {
    id: 'read',
    icon: BtnIcon.read,
    tooltip: btnText.read,
  },
  unread: {
    id: 'unread',
    icon: BtnIcon.unread,
    tooltip: btnText.unread,
  },
  archive: {
    id: 'archive',
    icon: BtnIcon.archive,
    tooltip: btnText.archive,
  },
  restore: {
    id: 'restore',
    icon: BtnIcon.restore,
    tooltip: btnText.restore,
  },
  edit: {
    id: 'edit',
    icon: BtnIcon.edit,
    tooltip: btnText.edit,
  },
  cancel: {
    id: 'cancel',
    icon: BtnIcon.cancel,
    tooltip: btnText.cancel,
  },
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get action text for a bulk action (for backward compatibility with Selected.tsx)
 */
export const getBulkActionText = (actionId: BulkActionId): ReactNode => {
  return BULK_ACTIONS[actionId]?.tooltip ?? '';
};

/**
 * Get action icon for a bulk action (for backward compatibility with Selected.tsx)
 */
export const getBulkActionIcon = (
  actionId: BulkActionId
): React.ComponentType<Record<string, unknown>> => {
  return BULK_ACTIONS[actionId]?.icon ?? BtnIcon.cancel;
};

// ============================================================================
// Actions Column Creation
// ============================================================================

/**
 * Creates an actions cell with icon buttons
 */
const createActionsCell = <T,>(actions: ActionConfig<T>[], row: T) => {
  return (
    <ActionsCell>
      {actions.map(action => {
        const button = (
          <Tooltip key={action.id} title={action.tooltip}>
            <IconButton onClick={() => action.onClick(row)} color={action.color}>
              <action.icon size={16} />
            </IconButton>
          </Tooltip>
        );

        if (action.requiresAccess) {
          return (
            <AccessControl
              key={action.id}
              write={action.requiresAccess.write}
              scope={action.requiresAccess.scope}
              fallback={null}
            >
              {button}
            </AccessControl>
          );
        }

        return button;
      })}
    </ActionsCell>
  );
};

/**
 * Creates standard actions column from handlers
 */
export const createStandardActionsColumn = <T extends Record<string, unknown>>(
  config: ActionsConfig<T>
): ColumnDef<T> => {
  // Get actions that have handlers
  const actionIds = Object.keys(config.handlers || {}) as StandardActionId[];
  const actions: ActionConfig<T>[] = actionIds.map(actionId => {
    const standardAction = STANDARD_ACTIONS[actionId];
    const requiresAccess = config.requiresAccess?.[actionId];
    const handler = config.handlers?.[actionId];
    if (!handler) {
      throw new Error(`Handler for action ${actionId} is required`);
    }
    return {
      ...standardAction,
      onClick: handler,
      requiresAccess,
    };
  });

  return {
    accessorKey: 'actions',
    id: 'actions',
    header: t`Actions`,
    enableSorting: false,
    enableResizing: false,
    minSize: 80,
    size: 80,
    maxSize: 100,
    cell: ({ row }: CellContext<T, unknown>) => createActionsCell<T>(actions, row.original),
  };
};
